import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Button,
    TextField,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { API_URL } from '../../api';
import { useConfirm } from '../../components/ConfirmDialog';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

export default function InvoiceLineTemplatesPage() {
    const confirm = useConfirm();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);

    const [editId, setEditId] = useState(null);
    const [editText, setEditText] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/invoice-line-templates`,
                authHeaders()
            );
            setItems(Array.isArray(data) ? data : []);
        } catch {
            setError('Failed to load.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const addPreset = async (e) => {
        e?.preventDefault?.();
        const description = draft.trim();
        if (!description) return;
        setBusy(true);
        setError('');
        try {
            await axios.post(
                `${API_URL}/api/admin/invoice-line-templates`,
                { description, sortOrder: items.length },
                authHeaders()
            );
            setDraft('');
            await load();
        } catch {
            setError('Failed to add.');
        } finally {
            setBusy(false);
        }
    };

    const startEdit = (item) => {
        setEditId(item._id);
        setEditText(item.description);
    };

    const cancelEdit = () => {
        setEditId(null);
        setEditText('');
    };

    const saveEdit = async (id) => {
        const description = editText.trim();
        if (!description) return;
        try {
            await axios.patch(
                `${API_URL}/api/admin/invoice-line-templates/${id}`,
                { description },
                authHeaders()
            );
            cancelEdit();
            await load();
        } catch {
            setError('Failed to save.');
        }
    };

    const remove = async (id) => {
        if (!(await confirm({ title: 'Delete preset', message: 'Delete this invoice line preset?', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(
                `${API_URL}/api/admin/invoice-line-templates/${id}`,
                authHeaders()
            );
            await load();
        } catch {
            setError('Failed to delete.');
        }
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Invoice descriptions</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 640 }}>
                        Pre-written line-item descriptions you can pick from when creating an invoice.
                        Free-text entry on the invoice form still works — these are just shortcuts for
                        the services you bill most often.
                    </Typography>
                    
                </Box>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> These are canned invoice line descriptions you can pick from the dropdown on the New invoice page — saves re-typing "Autumn term — Year 7 English (10 sessions × £30)".<br />
                            <strong>2.</strong> Click <strong>New description</strong> to add one; edit or delete via the row buttons.<br />
                            <strong>3.</strong> Editing a description here doesn't rewrite invoices that already used the old wording — invoices snapshot their line items at send time.<br />
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Card sx={{ p: 2, mb: 3 }}>
                <Box component="form" onSubmit={addPreset} sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="New description"
                        size="small"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        sx={{ flex: 1 }}
                    />
                    <Button type="submit" variant="contained" disabled={busy || !draft.trim()}>
                        Add
                    </Button>
                </Box>
            </Card>

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Description</TableCell>
                                <TableCell align="right" sx={{ width: 140 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item._id}>
                                    <TableCell>
                                        {editId === item._id ? (
                                            <TextField
                                                value={editText}
                                                onChange={(e) => setEditText(e.target.value)}
                                                size="small"
                                                fullWidth
                                                autoFocus
                                            />
                                        ) : (
                                            item.description
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {editId === item._id ? (
                                            <>
                                                <IconButton onClick={() => saveEdit(item._id)} color="primary">
                                                    <SaveIcon />
                                                </IconButton>
                                                <IconButton onClick={cancelEdit}>
                                                    <CloseIcon />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <>
                                                <IconButton onClick={() => startEdit(item)}>
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton onClick={() => remove(item._id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={2}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No presets yet. Add your common line-item descriptions above.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Card>
        </Container>
    );
}